import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LayoutContext } from '../../contexts/layout.context';
import { authSelectors } from '../../store/selectors/auth';
import { profileSelectors } from '../../store/selectors/profile';
import { getToken } from '../../utils/token';
import { saveTrainingResult, verbTraining } from '../../api/training';
import { initSound, playSfx, unlockAudio } from '../../utils/sounds';
import SoundToggle from '../../common/SoundToggle/SoundToggle';
import SpeakButton from '../../common/SpeakButton/SpeakButton';
import Spinner from '../../common/Spinner/Spinner';
import { Confetti, TrainingToasts, announceAchievements, useToasts } from '../../components/Training/TrainingFx';
import './verb-training.scss';

const ACCENTS = {
  fr: ['é', 'è', 'ê', 'à', 'ç', 'ô', 'î', 'û'],
  it: ['à', 'è', 'é', 'ì', 'ò', 'ù'],
  tr: ['ç', 'ğ', 'ı', 'İ', 'ö', 'ş', 'ü'],
  es: ['á', 'é', 'í', 'ó', 'ú', 'ñ', 'ü'],
  de: ['ä', 'ö', 'ü', 'ß'],
  pl: ['ą', 'ć', 'ę', 'ł', 'ń', 'ó', 'ś', 'ź', 'ż'],
};

const PRAISE = ['Чудово!', 'Так тримати!', 'Супер!', 'Влучно!', 'Молодець!', 'Ідеально!', 'Без помилок!'];
const COMBO_STEPS = [3, 5, 8, 12];

const clean = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const plain = (value) => clean(value).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ı/g, 'i').replace(/ł/g, 'l').replace(/ß/g, 'ss');

const verdict = (input, answer) => {
  if (clean(input) === clean(answer)) return 'ok';
  if (plain(input) !== '' && plain(input) === plain(answer)) return 'near';
  return 'bad';
};

const tasksWord = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'завдання';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'завдання';
  return 'завдань';
};

const readSavedUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.id;
  } catch (e) {
    return undefined;
  }
};

const VerbTraining = () => {
  const navigate = useNavigate();
  const { setLayout } = useContext(LayoutContext);
  const auth = useSelector(authSelectors);
  const profile = useSelector(profileSelectors);
  const token = auth.token;
  const studentId = profile?.user?.id || readSavedUserId();

  const [state, setState] = useState({ load: true, error: '', data: null });
  const [stage, setStage] = useState('intro');
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ first: 0, firstOk: 0, combo: 0, maxCombo: 0 });
  const [burst, setBurst] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [confetti, setConfetti] = useState(null);
  const [toasts, pushToast] = useToasts();
  const startedAt = useRef(0);
  const seenIds = useRef(new Set());
  const inputRef = useRef(null);

  getToken();

  useEffect(() => {
    document.title = 'Тренування дієслів | New Brain';
    setLayout({ page: 'Тренування дієслів' });
  }, [setLayout]);

  useEffect(() => {
    initSound(studentId);
  }, [studentId]);

  const load = useCallback(() => {
    if (!token) return;
    setState({ load: true, error: '', data: null });
    verbTraining({ token })
      .then((res) => {
        setState({ load: false, error: '', data: res.data.data });
        setStage('intro');
      })
      .catch((error) => {
        setState({ load: false, error: error?.response?.data?.info?.message || 'Не вдалося завантажити тренування. Спробуйте ще раз.', data: null });
      });
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const data = state.data;
  const current = queue[index] || null;
  const lang = data?.tts_lang || 'en-GB';
  const accents = ACCENTS[data?.lang] || [];

  const start = () => {
    unlockAudio();
    playSfx('tap');
    seenIds.current = new Set();
    startedAt.current = Date.now();
    setQueue(data.exercises.map((item) => ({ ...item, retry: false })));
    setIndex(0);
    setAnswer('');
    setResult(null);
    setRevealed(false);
    setStats({ first: 0, firstOk: 0, combo: 0, maxCombo: 0 });
    setStage('play');
  };

  const finishCheck = (outcome, given) => {
    const isRight = outcome !== 'bad';
    const firstTry = !seenIds.current.has(current.id);
    seenIds.current.add(current.id);

    const combo = isRight ? stats.combo + 1 : 0;
    if (isRight && COMBO_STEPS.includes(combo)) {
      setBurst(combo);
      playSfx('combo');
      setTimeout(() => setBurst(null), 1300);
    }
    setStats({
      first: stats.first + (firstTry ? 1 : 0),
      firstOk: stats.firstOk + (firstTry && isRight ? 1 : 0),
      combo,
      maxCombo: Math.max(stats.maxCombo, combo),
    });

    playSfx(isRight ? 'ok' : 'bad');
    setResult({
      outcome,
      given,
      praise: PRAISE[Math.floor(Math.random() * PRAISE.length)],
    });

    if (!isRight && !current.retry) {
      setQueue((prev) => [...prev, { ...current, retry: true }]);
    }
  };

  const choose = (option) => {
    if (result) return;
    finishCheck(clean(option) === clean(current.answer) ? 'ok' : 'bad', option);
  };

  const check = () => {
    if (result || answer.trim() === '') return;
    finishCheck(verdict(answer, current.answer), answer);
  };

  const saveResult = (accuracy) => {
    const seconds = Math.round((Date.now() - startedAt.current) / 1000);
    saveTrainingResult({
      token,
      result: {
        game: 'verbs',
        lang: data.lang,
        score: stats.firstOk,
        correct: stats.firstOk,
        total: stats.first,
        max_combo: stats.maxCombo,
        seconds: Math.min(seconds, 3600),
        accuracy,
      },
    })
      .then((res) => announceAchievements(res.data.data.new, pushToast))
      .catch(() => pushToast('Не вдалося зберегти результат. Перевірте інтернет.', 'red'));
  };

  const next = () => {
    playSfx('tap');
    if (index + 1 >= queue.length) {
      const accuracy = stats.first ? Math.round((stats.firstOk / stats.first) * 100) : 0;
      if (accuracy >= 80) {
        playSfx('win');
        setConfetti({ id: Date.now(), count: accuracy === 100 ? 200 : 110 });
      }
      setStage('done');
      saveResult(accuracy);
      return;
    }
    setIndex(index + 1);
    setAnswer('');
    setResult(null);
    setRevealed(false);
  };

  useEffect(() => {
    if (stage === 'play' && current && current.type !== 'choose' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [stage, index, current]);

  const insertAccent = (char) => {
    const input = inputRef.current;
    if (!input || result) return;
    const startPos = input.selectionStart ?? answer.length;
    const endPos = input.selectionEnd ?? answer.length;
    const value = answer.slice(0, startPos) + char + answer.slice(endPos);
    setAnswer(value);
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(startPos + char.length, startPos + char.length);
    });
  };

  const progress = queue.length ? Math.round((index / queue.length) * 100) : 0;
  const seconds = stage === 'done' ? Math.round((Date.now() - startedAt.current) / 1000) : 0;
  const accuracy = stats.first ? Math.round((stats.firstOk / stats.first) * 100) : 0;

  const renderGapInput = () => (
    <input
      ref={inputRef}
      type="text"
      className={`vt-gap ${result ? `is-${result.outcome}` : ''}`.trim()}
      value={answer}
      onChange={(event) => setAnswer(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (result) next();
          else check();
        }
      }}
      readOnly={Boolean(result)}
      autoComplete="off"
      autoCapitalize="off"
      spellCheck="false"
      aria-label="Форма дієслова"
      size={Math.max(4, (current?.answer || '').length + 1)}
    />
  );

  if (state.load) {
    return <Spinner />;
  }

  const back = () => navigate('/homework');

  if (state.error || !data?.available) {
    return (
      <div className="vt container">
        <div className="vt-card vt-card--center">
          <div className="vt-emoji" aria-hidden="true">🗣️</div>
          <h1 className="vt-title">Тренування дієслів</h1>
          <p className="vt-text">{state.error || data?.message}</p>
          <button type="button" className="vt-btn vt-btn--light" onClick={state.error ? load : back}>
            {state.error ? 'Спробувати ще раз' : 'Назад'}
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'intro') {
    return (
      <div className="vt container">
        <div className="vt-top">
          <button type="button" className="vt-back" onClick={back} aria-label="Назад">‹</button>
          <SoundToggle />
        </div>
        <div className="vt-card vt-card--center">
          <div className="vt-emoji" aria-hidden="true">🗣️</div>
          <h1 className="vt-title">Тренування дієслів</h1>
          <p className="vt-text">{data.exercises.length} {tasksWord(data.exercises.length)} на часи, які ви вже пройшли. Помилки повторимо в кінці.</p>
          <div className="vt-chips">
            {data.tenses.map((tense) => <span key={tense} className="vt-chip">{tense}</span>)}
          </div>
          <button type="button" className="vt-btn vt-btn--main" onClick={start}>Почати</button>
        </div>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className="vt container">
        <Confetti burst={confetti} />
        <TrainingToasts toasts={toasts} />
        <div className="vt-card vt-card--center vt-result">
          <div className="vt-emoji vt-emoji--jump" aria-hidden="true">{accuracy >= 80 ? '🏆' : '💪'}</div>
          <h1 className="vt-title">{accuracy >= 80 ? 'Чудовий результат!' : 'Тренування завершено'}</h1>
          <div className="vt-stats">
            <div><b>{accuracy}%</b><span>з першої спроби</span></div>
            <div><b>🔥 {stats.maxCombo}</b><span>поспіль</span></div>
            <div><b>{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</b><span>час</span></div>
          </div>
          <button type="button" className="vt-btn vt-btn--main" onClick={load}>Ще раз — нові завдання</button>
          <button type="button" className="vt-btn vt-btn--light" onClick={back}>До домашніх завдань</button>
        </div>
      </div>
    );
  }

  const isChoose = current.type === 'choose';
  const isDialog = current.type === 'dialog';

  return (
    <div className="vt container">
      <div className="vt-top">
        <button type="button" className="vt-back" onClick={back} aria-label="Завершити тренування">×</button>
        <div className={`vt-progress ${stats.combo >= 3 ? 'is-hot' : ''}`.trim()} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <span style={{ width: `${progress}%` }} />
        </div>
        {stats.combo >= 3 && <span className="vt-combo" key={stats.combo}>🔥{stats.combo}</span>}
        <SoundToggle />
      </div>
      {burst && <div className="vt-burst" aria-live="polite">🔥 {burst} поспіль!</div>}

      <div className="vt-card vt-task" key={`${current.id}-${index}`}>
        <div className="vt-kind">
          {isChoose && 'Обери форму'}
          {current.type === 'gap' && 'Речення з пропуском'}
          {isDialog && 'Відповідь у діалозі'}
          {current.retry && <span className="vt-retry">повтор</span>}
        </div>
        <div className="vt-chips">
          <span className="vt-chip">{current.tense}</span>
          <span className="vt-chip vt-chip--light">{current.infinitive}{current.translation && isChoose ? ` — ${current.translation}` : ''}</span>
        </div>

        {isChoose && (
          <>
            <div className="vt-prompt">
              <span className="vt-person">{current.person}</span>
              <span className={`vt-blank ${result ? `is-${result.outcome}` : ''}`.trim()}>{result ? current.answer : '…'}</span>
            </div>
            <div className="vt-options">
              {current.options.map((option, optionIndex) => {
                const isAnswer = clean(option) === clean(current.answer);
                const isPicked = result && clean(option) === clean(result.given);
                const cls = result ? (isAnswer ? 'is-ok' : (isPicked ? 'is-bad' : '')) : '';
                return (
                  <button key={option} type="button" className={`vt-opt ${cls}`.trim()} onClick={() => choose(option)} disabled={Boolean(result)}>
                    <span className="vt-opt__num">{optionIndex + 1}</span>
                    <span className="vt-opt__text">{option}</span>
                    {result && isAnswer && <span className="vt-opt__mark" aria-label="правильно">✓</span>}
                    {result && isPicked && !isAnswer && <span className="vt-opt__mark" aria-label="помилка">✕</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {current.type === 'gap' && (
          <>
            <div className="vt-plate">{current.translation}</div>
            <div className="vt-sentence">
              <span>{current.before}</span>{renderGapInput()}<span>{current.after}</span>
              <SpeakButton text={result ? current.sentence : ''} lang={lang} size="md" className="vt-speak" />
            </div>
          </>
        )}

        {isDialog && (
          <div className="vt-dialog">
            <div className="vt-bubble vt-bubble--left">
              <div className="vt-bubble__text">{current.question}<SpeakButton text={current.question} lang={lang} className="vt-speak" /></div>
              {current.question_translation && (
                <button type="button" className={`vt-reveal ${revealed ? 'is-open' : ''}`.trim()} onClick={() => setRevealed(true)} aria-label="Показати переклад питання">
                  {current.question_translation}
                </button>
              )}
            </div>
            <div className="vt-bubble vt-bubble--right">
              <div className="vt-bubble__text"><span>{current.before}</span>{renderGapInput()}<span>{current.after}</span></div>
            </div>
          </div>
        )}

        {!isChoose && accents.length > 0 && !result && (
          <div className="vt-accents" aria-label="Літери з наголосами">
            {accents.map((char) => (
              <button key={char} type="button" className="vt-accent" onMouseDown={(event) => event.preventDefault()} onClick={() => insertAccent(char)}>{char}</button>
            ))}
          </div>
        )}

        {!isChoose && !result && (
          <button type="button" className="vt-btn vt-btn--main" onClick={check} disabled={answer.trim() === ''}>Перевірити</button>
        )}
      </div>

      {result && (
        <div className={`vt-sheet is-${result.outcome}`} role="status">
          <div className="vt-sheet__title">
            {result.outcome === 'ok' && <>✓ {result.praise}</>}
            {result.outcome === 'near' && <>≈ Майже! Перевірте наголос</>}
            {result.outcome === 'bad' && <>✕ Не зовсім</>}
          </div>
          {result.outcome !== 'ok' && (
            <div className="vt-sheet__answer">Правильно: <b>{current.answer}</b></div>
          )}
          {result.outcome === 'bad' && !current.retry && <div className="vt-sheet__note">🔁 Повторимо це завдання в кінці</div>}
          <button type="button" className="vt-btn vt-btn--main" onClick={next}>Продовжити</button>
        </div>
      )}
    </div>
  );
};

export default VerbTraining;
